import { useState, useEffect, useCallback, useMemo } from 'react';
import { IconButton, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RecipeType } from '@/enums/recipe-type.enum';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { FolderScope } from '@/enums/folder-scope.enum';
import { log } from '@/logger/logger';
import { FlexCenVer, FlexCol, FlexColCenHorVer } from '@/UI/styles';
import { Input } from '@/UI/Input/Input';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { SearchIcon } from '@/UI/Icons/SearchIcon';
import { SmileyXEyes } from '@/UI/Icons/SmileyXEyes';
import { YarnIcon } from '@/UI/Icons/YarnIcon';
import { AppWindowIcon } from '@/UI/Icons/AppWindowIcon';
import { FolderEmptyIcon } from '@/UI/Icons/FolderEmptyIcon';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { DashboardSection } from '@/components/Dashboard/enums/shared';
import { useGlobalStore } from '@/state/global.state';
import { useUserStore } from '@/state/user.state';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { FileIcon } from '@/UI/Icons/FileIcon';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { useCreateRecipe, useDeleteRecipe, useDuplicateRecipe, useRenameRecipe } from '@/queries/useRecipes';
import { useCreateFolder, useRenameFolder, useMoveFolder, useDeleteFolder, useMoveRecipe } from '@/queries/useFolders';
import { getApiErrorMessage } from '@/utils/api.utils';
import { goToRecipe } from '@/utils/recipes';
import ConfirmationDialogV2 from '../Common/ConfirmationDialogV2';
import { MenuAction } from '../Menu/Actions';
import { ErrorDisplay } from '../Common/Error/ErrorDisplay';
import { EmptyState } from '../Common/EmptyState/EmptyState';
import { countFilesInFolder } from './utils/dashboard.utils';
import DashboardListView from './DashboardListView';
import EducationGallery from './EducationGallery';
import { DashboardCardView } from './DashboardCardView';
import ChangeOwnerModal from './ChangeOwner/ChangeOwnerModal';
import { CreateFolderModal } from './CreateFolderModal';
import { MoveModal } from './MoveModal';
import { Breadcrumbs } from './Breadcrumbs';
import type { DashboardRecipe } from '@/types/recipes';
import type { FolderResponseDto } from '@/types/folder.types';

interface DashboardMainProps {
  recipes: DashboardRecipe[];
  title: string;
  showCase: DashboardRecipe[];
  section: DashboardSection;
  isError: boolean;
  onReload: () => void;
  onSectionChange?: (section: DashboardSection) => void;
  onFolderNavigate?: (folderId?: string | null) => void;
  showChangeNameButton?: boolean;
  currentFolderId?: string | null;
  folders?: FolderResponseDto[];
  folderPath?: FolderResponseDto[];
}

type DeleteDialogState = { kind: 'closed' } | { kind: 'recipe'; id: string } | { kind: 'folder'; id: string };
type MoveDialogState =
  | { kind: 'closed' }
  | { kind: 'recipe'; id: string; name: string }
  | { kind: 'folder'; id: string; name: string };
type ChangeOwnerDialogState = { kind: 'closed' } | { kind: 'open'; id: string };
export type EditingNameState = { kind: 'closed' } | { kind: 'editing'; id: string };

export function DashboardMain({
  recipes,
  title,
  showCase,
  section,
  isError,
  onReload,
  onSectionChange,
  onFolderNavigate,
  showChangeNameButton = false,
  currentFolderId,
  folders = [],
  folderPath = [],
}: DashboardMainProps) {
  const { updateSnackbarData } = useGlobalStore();
  const { track } = useAnalytics();
  const [isListView, setIsListView] = useState<boolean>(() => {
    const saved = localStorage.getItem('isListView');
    return saved !== null ? saved === 'true' : false;
  });

  const [recipeEditState, setRecipeEditState] = useState<EditingNameState>({ kind: 'closed' });
  const [folderEditState, setFolderEditState] = useState<EditingNameState>({ kind: 'closed' });
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>({ kind: 'closed' });
  const [changeOwnerDialogState, setChangeOwnerDialogState] = useState<ChangeOwnerDialogState>({ kind: 'closed' });
  const [isShowingCreateFolderDialog, setIsShowingCreateFolderDialog] = useState(false);
  const [moveDialogState, setMoveDialogState] = useState<MoveDialogState>({ kind: 'closed' });
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number | null;
    mouseY: number | null;
    isOpen: boolean;
    recipeId: string | null;
    isFolder: boolean;
  }>({ mouseX: null, mouseY: null, isOpen: false, recipeId: null, isFolder: false });
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();
  const currentUserId = useUserStore((state) => state.user?.uid);
  const { mutate: createRecipe } = useCreateRecipe();
  const { mutateAsync: duplicateRecipe } = useDuplicateRecipe();
  const { mutateAsync: deleteRecipe } = useDeleteRecipe();
  const { mutateAsync: renameRecipe } = useRenameRecipe();
  const isSharedApps = section === DashboardSection.SharedApps;
  const isWorkspaceFiles = section === DashboardSection.WorkspaceFiles;
  const isSharedWithMe = section === DashboardSection.SharedWithMe;

  const currentScope = isWorkspaceFiles ? FolderScope.WORKSPACE : FolderScope.PERSONAL;

  // Mutation hooks
  const { mutateAsync: createFolderMutation } = useCreateFolder();
  const { mutateAsync: renameFolderMutation } = useRenameFolder();
  const { mutateAsync: moveFolderMutation } = useMoveFolder();
  const { mutateAsync: deleteFolderMutation } = useDeleteFolder();
  const { mutateAsync: moveRecipeMutation } = useMoveRecipe();

  useEffect(() => {
    localStorage.setItem('isListView', isListView.toString());
  }, [isListView]);

  // Clear search term when section changes
  useEffect(() => {
    setSearchTerm('');
  }, [section]);

  const handleDragAndDropMove = useCallback(
    async (itemId: string, itemType: 'file' | 'folder', targetFolderId: string) => {
      if (itemType === 'file') {
        const movedRecipe = recipes.find((r) => r.id === itemId);
        if (!movedRecipe) return;

        try {
          await moveRecipeMutation({
            id: itemId,
            folderId: targetFolderId,
            scope: currentScope,
            sourceScope: currentScope,
          });
        } catch (error) {
          updateSnackbarData({
            text: `${t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.ERROR_PREFIX)} ${getApiErrorMessage(error, `Failed to move ${itemType}`)}`,
            isOpen: true,
            icon: null,
            severity: 'error',
          });
        }
      } else {
        const movedFolder = folders?.find((f) => f.id === itemId);
        if (!movedFolder) return;

        const isDescendant = (parentId: string | null | undefined, childId: string): boolean => {
          if (!parentId) return false;
          if (parentId === childId) return true;
          const parent = folders?.find((f) => f.id === parentId);
          return parent ? isDescendant(parent.parentId, childId) : false;
        };

        if (isDescendant(targetFolderId, itemId)) {
          updateSnackbarData({
            text: 'Cannot move a folder into its own subfolder',
            isOpen: true,
            icon: null,
          });
          return;
        }

        try {
          await moveFolderMutation({
            id: itemId,
            parentId: targetFolderId,
            scope: currentScope,
            sourceScope: currentScope,
          });
        } catch (error) {
          updateSnackbarData({
            text: `${t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.ERROR_PREFIX)} ${getApiErrorMessage(error, `Failed to move ${itemType}`)}`,
            isOpen: true,
            icon: null,
            severity: 'error',
          });
        }
      }
    },
    [currentScope, recipes, folders, moveRecipeMutation, moveFolderMutation, updateSnackbarData, t],
  );

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(evt.target.value);

  // Helper function to get all descendant folder IDs recursively
  const getAllDescendantFolderIds = (folderId: string | null, folders: FolderResponseDto[]): string[] => {
    const childFolders = folders.filter((folder) => folder.parentId === folderId);
    let descendants: string[] = [];

    for (const child of childFolders) {
      descendants.push(child.id);
      descendants = descendants.concat(getAllDescendantFolderIds(child.id, folders));
    }

    return descendants;
  };

  // Helper function to search in nested folders
  const searchInNestedFolders = (folders: FolderResponseDto[], searchTerm: string): FolderResponseDto[] => {
    if (!searchTerm) {
      // No search - only show folders directly in current directory
      return folders.filter((folder) => folder.parentId === currentFolderId);
    }

    // With search - get all folder IDs that are descendants of current folder (including current folder)
    const folderIdsInScope = currentFolderId
      ? [currentFolderId, ...getAllDescendantFolderIds(currentFolderId, folders)]
      : [null, ...getAllDescendantFolderIds(null, folders)];

    // Show matching folders whose parentId is in scope (i.e., they are within the current folder tree)
    return folders.filter(
      (folder) =>
        folderIdsInScope.includes(folder.parentId || null) &&
        folder.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  // Filter recipes by current folder and search term
  const filteredRecipes = recipes?.filter((recipe) => {
    const matchesSearch = recipe.name.toLowerCase().includes(searchTerm.toLowerCase());

    if (searchTerm) {
      // When searching, include recipes from current folder AND all nested subfolders
      if (currentFolderId) {
        const descendantIds = getAllDescendantFolderIds(currentFolderId, folders);
        const folderIdsInScope = [currentFolderId, ...descendantIds];
        return recipe.folderId && folderIdsInScope.includes(recipe.folderId) && matchesSearch;
      } else {
        // At root with search - search in all recipes
        const allFolderIds = getAllDescendantFolderIds(null, folders);
        const folderIdsInScope = [null, ...allFolderIds];
        return (folderIdsInScope.includes(recipe.folderId) || !recipe.folderId) && matchesSearch;
      }
    } else {
      // No search - only show recipes directly in current folder/location
      if (currentFolderId) {
        return recipe.folderId === currentFolderId;
      } else {
        return !recipe.folderId;
      }
    }
  });
  const filteredFolders = isSharedWithMe || isSharedApps ? [] : searchInNestedFolders(folders || [], searchTerm);

  const handleContextMenu = (evt: React.MouseEvent<HTMLDivElement>, id: string, itemType?: 'folder' | 'recipe') => {
    evt.preventDefault();
    evt.stopPropagation();

    // Don't show context menu in Shared with Me section unless it's for a specific recipe
    if (isSharedWithMe && !id) {
      return;
    }

    // Use the provided itemType if available, otherwise check if this ID belongs to a folder
    const isFolder = itemType === 'folder' || (!itemType && (folders?.some((folder) => folder.id === id) || false));

    const newContextMenu = {
      mouseX: evt.clientX - 2,
      mouseY: evt.clientY - 4,
      isOpen: true,
      recipeId: id,
      isFolder,
    };

    setContextMenu(newContextMenu);
  };

  const handleGeneralContextMenu = (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.preventDefault();
    setContextMenu({
      mouseX: evt.clientX - 2,
      mouseY: evt.clientY - 4,
      isOpen: true,
      recipeId: null,
      isFolder: false,
    });
  };

  const handleContextMenuClose = useCallback(() => {
    setContextMenu({ mouseX: null, mouseY: null, isOpen: false, recipeId: null, isFolder: false });
  }, []);

  const handleDuplicateRecipe = useCallback(
    async (id: string) => {
      try {
        await duplicateRecipe(id);
      } catch (error) {
        log.error('error duplicating recipe: ', error);
      }
    },
    [duplicateRecipe],
  );

  const enableRecipeNameEdit = useCallback(
    (id: string) => {
      const recipeToRename = recipes?.filter((rcp) => rcp.id === id);
      if (recipeToRename.length) {
        setFolderEditState({ kind: 'closed' });
        setRecipeEditState({ kind: 'editing', id });
      }
    },
    [recipes],
  );

  const enableFolderNameEdit = useCallback(
    (id: string) => {
      const folderToRename = folders?.find((folder) => folder.id === id);
      if (folderToRename) {
        setRecipeEditState({ kind: 'closed' });
        setFolderEditState({ kind: 'editing', id });
      }
    },
    [folders],
  );

  const handleRenameRecipe = useCallback(
    async (id: string, newName: string) => {
      try {
        await renameRecipe({ id, newName });
        setRecipeEditState({ kind: 'closed' });
      } catch (error) {
        log.error('error renaming recipe: ', error);
        updateSnackbarData({
          text: getApiErrorMessage(error, 'Error renaming recipe'),
          isOpen: true,
          icon: null,
          severity: 'error',
        });
      }
    },
    [renameRecipe, updateSnackbarData],
  );

  const handleRenameFolder = useCallback(
    async (id: string, newName: string) => {
      try {
        await renameFolderMutation({ id, name: newName, scope: currentScope });
        setFolderEditState({ kind: 'closed' });
      } catch (error) {
        log.error('error renaming folder: ', error);
        updateSnackbarData({
          text: t(I18N_KEYS.MAIN_DASHBOARD.RENAME_FOLDER_ERROR),
          isOpen: true,
          icon: null,
          severity: 'error',
        });
      }
    },
    [renameFolderMutation, updateSnackbarData, t, currentScope],
  );

  const handleCancelRenameRecipe = useCallback(() => {
    setRecipeEditState({ kind: 'closed' });
  }, []);

  const handleCancelRenameFolder = useCallback((_id: string) => {
    setFolderEditState({ kind: 'closed' });
  }, []);

  const handleFolderNavigate = useCallback(
    (folderId?: string | null) => {
      setRecipeEditState({ kind: 'closed' });
      setFolderEditState({ kind: 'closed' });
      onFolderNavigate?.(folderId);
    },
    [onFolderNavigate],
  );

  const onDeleteRecipe = (id: string) => setDeleteDialogState({ kind: 'recipe', id });
  const onDeleteFolder = (id: string) => setDeleteDialogState({ kind: 'folder', id });

  const handleDeleteDialogClose = () => setDeleteDialogState({ kind: 'closed' });

  const handleConfirmDelete = async () => {
    if (deleteDialogState.kind === 'folder') {
      try {
        await deleteFolderMutation({ id: deleteDialogState.id, scope: currentScope });
        updateSnackbarData({
          text: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_FOLDER_SUCCESS),
          isOpen: true,
          icon: <CheckMarkCircleIcon height={20} width={20} />,
          severity: 'success',
        });
      } catch (error) {
        log.error('error deleting folder: ', error);
        updateSnackbarData({
          text: t(I18N_KEYS.MAIN_DASHBOARD.DELETE_FOLDER_ERROR),
          isOpen: true,
          icon: null,
          severity: 'error',
        });
      }
    } else if (deleteDialogState.kind === 'recipe') {
      try {
        await deleteRecipe({
          id: deleteDialogState.id,
          type: isSharedApps ? RecipeType.DesignApp : RecipeType.File,
        });
      } catch (error) {
        log.error('error deleting recipe: ', error);
      }
    }
    setDeleteDialogState({ kind: 'closed' });
  };

  const handleCreateFolder = async (folderName: string) => {
    try {
      await createFolderMutation({ name: folderName, scope: currentScope, parentId: currentFolderId });
      setIsShowingCreateFolderDialog(false);
      updateSnackbarData({
        text: t(I18N_KEYS.MAIN_DASHBOARD.CREATE_FOLDER_MODAL.SUCCESS),
        isOpen: true,
        icon: <CheckMarkCircleIcon height={20} width={20} />,
        severity: 'success',
      });
      track('create_folder_clicked', { source: 'dashboard_main_context_menu' }, TrackTypeEnum.BI);
    } catch (error) {
      updateSnackbarData({
        text: `${t(I18N_KEYS.MAIN_DASHBOARD.CREATE_FOLDER_MODAL.ERROR_PREFIX)} ${getApiErrorMessage(error, 'Failed to create folder')}`,
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    }
  };

  const handleGoToMyFiles = useCallback(() => {
    onSectionChange?.(DashboardSection.RecentRecipes);
  }, [onSectionChange]);

  const handleMove = async (targetFolderId: string | null, targetScope: FolderScope) => {
    if (moveDialogState.kind === 'closed') {
      return;
    }

    try {
      if (moveDialogState.kind === 'folder') {
        await moveFolderMutation({
          id: moveDialogState.id,
          parentId: targetFolderId,
          scope: targetScope,
          sourceScope: currentScope,
        });
        updateSnackbarData({
          text: t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.SUCCESS_FOLDER),
          isOpen: true,
          icon: <CheckMarkCircleIcon height={20} width={20} />,
          severity: 'success',
        });
      } else {
        await moveRecipeMutation({
          id: moveDialogState.id,
          folderId: targetFolderId,
          scope: targetScope,
          sourceScope: currentScope,
        });
        updateSnackbarData({
          text: t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.SUCCESS_FILE),
          isOpen: true,
          icon: <CheckMarkCircleIcon height={20} width={20} />,
          severity: 'success',
        });
      }
    } catch (error) {
      updateSnackbarData({
        text: `${t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.ERROR_PREFIX)} ${getApiErrorMessage(error, 'Failed to move item')}`,
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    } finally {
      setMoveDialogState({ kind: 'closed' });
    }
  };

  // Create menu items function that will be called fresh each time
  const getMenuItems = useCallback(
    (menuContext?: typeof contextMenu): MenuAction[] => {
      const currentContext = menuContext || contextMenu;
      const items: MenuAction[] = [];

      // Add Create Folder and Create File options (only in My Files and Workspace Files)
      if (!isSharedApps && !isSharedWithMe && !currentContext.isFolder && !currentContext.recipeId) {
        items.push({
          name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CREATE_FILE),
          action: () => {
            const scope = section === DashboardSection.WorkspaceFiles ? FolderScope.WORKSPACE : FolderScope.PERSONAL;
            void createRecipe({ openInNewTab: false, parentId: currentFolderId, scope });
            handleContextMenuClose();
          },
          icon: <FileIcon width={16} height={16} />,
        });
        items.push({
          name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CREATE_FOLDER),
          action: () => {
            setIsShowingCreateFolderDialog(true);
            handleContextMenuClose();
          },
          icon: <FolderEmptyIcon width={16} height={16} />,
        });
      }

      // Add folder-specific options if a folder is selected
      if (currentContext.recipeId && currentContext.isFolder) {
        const folder = folders.find((f) => f.id === currentContext.recipeId);

        if (items.length > 0) {
          items.push({ type: 'divider' });
        }

        // Add Open options for folders
        items.push(
          {
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN),
            action: () => {
              if (handleFolderNavigate && folder) {
                handleFolderNavigate(folder.id);
              }
              handleContextMenuClose();
            },
          },
          {
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN_NEW_TAB),
            action: () => {
              if (folder) {
                window.open(`/folders/${folder.id}`, '_blank');
              }
              handleContextMenuClose();
            },
          },
        );

        items.push({ type: 'divider' });

        // Add Rename for folders
        if (!isSharedApps && !isSharedWithMe) {
          items.push({
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.RENAME),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              enableFolderNameEdit(currentContext.recipeId);
              handleContextMenuClose();
            },
          });
        }

        // Add Move for folders
        if (!isSharedApps && !isSharedWithMe) {
          items.push({
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.MOVE),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              setMoveDialogState({
                kind: 'folder',
                id: currentContext.recipeId,
                name: folder?.name || '',
              });
              handleContextMenuClose();
            },
          });
        }

        // Add Delete for folders
        if (!isSharedApps && !isSharedWithMe) {
          items.push({ type: 'divider' });
          items.push({
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.DELETE),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              onDeleteFolder(currentContext.recipeId);
              handleContextMenuClose();
            },
          });
        }
      }

      // Add recipe-specific options if a recipe is selected (not a folder)
      if (currentContext.recipeId && !currentContext.isFolder) {
        const selectedRecipe = recipes.find((r) => r.id === currentContext.recipeId);
        const isRecipeOwner = selectedRecipe?.owner && selectedRecipe.owner === currentUserId;

        // Add divider if we already have items
        if (items.length > 0) {
          items.push({ type: 'divider' });
        }

        // Add Open options
        items.push(
          {
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN),
            action: () => {
              goToRecipe(currentContext.recipeId);
              handleContextMenuClose();
            },
          },
          {
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN_NEW_TAB),
            action: () => {
              goToRecipe(currentContext.recipeId, true);
              handleContextMenuClose();
            },
          },
        );

        items.push({ type: 'divider' });

        // Add Duplicate
        items.push({
          name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.DUPLICATE),
          action: () => {
            handleContextMenuClose();
            void handleDuplicateRecipe(currentContext.recipeId ?? '');
            if (section === DashboardSection.RecentRecipes || section === DashboardSection.WorkspaceFiles) {
              return;
            }
            updateSnackbarData({
              text: t(I18N_KEYS.DASHBOARD.DUPLICATE_SUCCESS),
              isOpen: true,
              action: {
                text: t(I18N_KEYS.DASHBOARD.DUPLICATE_SUCCESS_ACTION),
                onClick: handleGoToMyFiles,
              },
              icon: <CheckMarkCircleIcon height={20} width={20} />,
            });
          },
        });

        // Add Move (if allowed)
        if (!isSharedApps && !isSharedWithMe) {
          items.push({
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.MOVE),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              const recipe = recipes.find((r) => r.id === currentContext.recipeId);
              setMoveDialogState({
                kind: 'recipe',
                id: currentContext.recipeId,
                name: recipe?.name || '',
              });
              handleContextMenuClose();
            },
          });
        }

        // Add Rename (if allowed)
        if (!isSharedApps && !isSharedWithMe) {
          items.push({
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.RENAME),
            action: () => {
              enableRecipeNameEdit(currentContext.recipeId ?? '');
              handleContextMenuClose();
            },
          });
        }

        // Add Change Owner for workspace files or when showChangeNameButton is true
        const isWorkspaceFile = section === DashboardSection.WorkspaceFiles;

        if ((showChangeNameButton || (isWorkspaceFile && isRecipeOwner)) && !isSharedApps && !isSharedWithMe) {
          items.push({ type: 'divider' });
          items.push({
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CHANGE_OWNER),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              setChangeOwnerDialogState({ kind: 'open', id: currentContext.recipeId });
              handleContextMenuClose();
            },
          });
        }

        // Add Delete (if allowed)
        const canShowDeleteInCurrentSection = !isSharedWithMe && (!isWorkspaceFile || isRecipeOwner);
        if (canShowDeleteInCurrentSection) {
          items.push({ type: 'divider' });
          items.push({
            name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.DELETE),
            action: () => {
              if (!currentContext.recipeId) {
                return;
              }
              onDeleteRecipe(currentContext.recipeId);
              handleContextMenuClose();
            },
          });
        }
      }

      return items;
    },
    [
      folders,
      contextMenu,
      currentFolderId,
      createRecipe,
      currentUserId,
      enableFolderNameEdit,
      enableRecipeNameEdit,
      handleContextMenuClose,
      handleDuplicateRecipe,
      handleFolderNavigate,
      handleGoToMyFiles,
      isSharedApps,
      isSharedWithMe,
      recipes,
      section,
      showChangeNameButton,
      t,
      updateSnackbarData,
    ],
  );

  const renderSubheader = () =>
    recipes?.length || folders?.length ? (
      <FlexCenVer
        data-testid="dashboard-top-menu-container"
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '40px',
          flexShrink: 0,
          mt: '32px',
          mb: '32px',
        }}
      >
        <Breadcrumbs folderPath={folderPath} onNavigate={handleFolderNavigate} rootName={title} />

        <FlexCenVer data-testid="dashboard-list-top-menu" sx={{ justifyContent: 'flex-end' }}>
          <Input
            placeholder={t(I18N_KEYS.GENERAL.SEARCH)}
            value={searchTerm}
            onChange={handleSearchChange}
            startAdornment={<SearchIcon />}
            sx={{ mr: 2 }}
          />

          <IconButton
            size="small"
            onClick={() => setIsListView(true)}
            sx={{ backgroundColor: isListView ? color.Super_Light_Transparent : '', borderRadius: 1 }}
          >
            <img src="/icons/list.svg" alt="list" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setIsListView(false)}
            sx={{ backgroundColor: !isListView ? color.Super_Light_Transparent : '', borderRadius: 1 }}
          >
            <img src="/icons/squares.svg" alt="squares" />
          </IconButton>
        </FlexCenVer>
      </FlexCenVer>
    ) : null;

  const renderContent = () => {
    if (filteredRecipes.length === 0 && filteredFolders.length === 0) {
      // Check if there's a search with no results (at any level - inside folder or at root)
      if (searchTerm) {
        return (
          <EmptyState
            icon={<FolderEmptyIcon />}
            title={t(I18N_KEYS.MAIN_DASHBOARD.SEARCH_NO_RESULTS.TITLE, { searchTerm })}
            description={t(I18N_KEYS.MAIN_DASHBOARD.SEARCH_NO_RESULTS.DESCRIPTION)}
            actions={[]}
            sx={{ paddingTop: '104px', alignItems: 'flex-start' }}
          />
        );
      }

      // Check if we're inside a folder and it's empty (no search active)
      if (currentFolderId) {
        return (
          <EmptyState
            icon={<FolderEmptyIcon />}
            title={t(I18N_KEYS.MAIN_DASHBOARD.FOLDER_EMPTY.TITLE)}
            description={t(I18N_KEYS.MAIN_DASHBOARD.FOLDER_EMPTY.DESCRIPTION)}
            actions={[]}
            sx={{ paddingTop: '104px', alignItems: 'flex-start' }}
          />
        );
      }
    }

    // Check if the root level is empty
    if (!recipes.length && !folders.length) {
      const icon = isSharedApps ? <AppWindowIcon /> : <YarnIcon />;
      const title = isSharedApps
        ? t(I18N_KEYS.DASHBOARD.PAGES.SHARED_APPS.EMPTY_STATE.TITLE)
        : isWorkspaceFiles
          ? t(I18N_KEYS.DASHBOARD.PAGES.WORKSPACE_FILES.EMPTY_STATE.TITLE)
          : t(I18N_KEYS.DASHBOARD.PAGES.FILES.EMPTY_STATE.TITLE);
      const description = isSharedApps
        ? null
        : isWorkspaceFiles
          ? 'Files added here will be shared with all workspace\u00A0members'
          : t(I18N_KEYS.DASHBOARD.PAGES.FILES.EMPTY_STATE.DESCRIPTION);
      const actions = isSharedApps
        ? [
            {
              label: t(I18N_KEYS.DASHBOARD.PAGES.SHARED_APPS.EMPTY_STATE.ACTION),
              href: EXTERNAL_LINKS.designAppTutorial,
              target: '_blank',
            },
          ]
        : isWorkspaceFiles
          ? [] // No actions for workspace files
          : [
              {
                label: t(I18N_KEYS.MAIN_DASHBOARD.CREATE_NEW_FILE),
                onClick: () => {
                  track('created_new_flow', { source: 'dashboard_empty_state' }, TrackTypeEnum.BI);
                  void createRecipe();
                },
              },
            ];
      return (
        <EmptyState
          icon={icon}
          title={title}
          description={description}
          actions={actions}
          sx={{ paddingTop: 'max(15%, 144px)', alignItems: 'flex-start' }}
        />
      );
    }
    if (isListView) {
      return (
        <DashboardListView
          recipes={filteredRecipes} // REMOVE THIS WHEN FILTER WILL BE IN THE SERVER
          allRecipes={recipes}
          folders={filteredFolders}
          allFolders={folders}
          handleRenameRecipe={(id, name) => void handleRenameRecipe(id, name)}
          handleContextMenu={handleContextMenu}
          recipeEditState={recipeEditState}
          cancelRename={handleCancelRenameRecipe}
          onFolderNavigate={handleFolderNavigate}
          currentFolderId={currentFolderId}
          isSearching={searchTerm.length > 0}
          folderEditState={folderEditState}
          handleRenameFolder={handleRenameFolder ? (id, name) => void handleRenameFolder(id, name) : undefined}
          cancelRenameFolder={handleCancelRenameFolder}
          countFilesInFolder={(folderId) =>
            countFilesInFolder(
              folderId,
              folders || [],
              (recipes || []).map((r) => ({ id: r.id, parentId: r.folderId })),
            )
          }
          onMoveItem={(itemId, itemType, targetFolderId) => {
            void handleDragAndDropMove(itemId, itemType, targetFolderId);
          }}
          isWorkspaceFiles={isWorkspaceFiles || isSharedWithMe}
        />
      );
    }
    return (
      <DashboardCardView
        recipes={filteredRecipes}
        allRecipes={recipes}
        folders={filteredFolders}
        allFolders={folders}
        handleRenameRecipe={(id, name) => void handleRenameRecipe(id, name)}
        handleContextMenu={handleContextMenu}
        recipeEditState={recipeEditState}
        cancelRename={handleCancelRenameRecipe}
        onFolderNavigate={handleFolderNavigate}
        currentFolderId={currentFolderId}
        isSearching={searchTerm.length > 0}
        folderEditState={folderEditState}
        handleRenameFolder={handleRenameFolder ? (id, name) => void handleRenameFolder(id, name) : undefined}
        cancelRenameFolder={handleCancelRenameFolder}
        countFilesInFolder={(folderId) =>
          countFilesInFolder(
            folderId,
            folders || [],
            (recipes || []).map((r) => ({ id: r.id, parentId: r.folderId })),
          )
        }
        onMoveItem={(itemId, itemType, targetFolderId) => {
          void handleDragAndDropMove(itemId, itemType, targetFolderId);
        }}
        isWorkspaceFiles={isWorkspaceFiles || isSharedWithMe}
      />
    );
  };

  return (
    <>
      <Box
        component="main"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          pb: 0,
        }}
        onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
          // Don't show context menu in Shared with Me section
          if (isSharedWithMe) {
            return;
          }

          // Handle right-click on the main container or any empty space
          const target = e.target as HTMLElement;
          const isEmptySpace =
            target === e.currentTarget ||
            target.closest('[data-testid="dashboard-table-view-container"]') ||
            target.closest('[data-testid="dashboard-card-view-container"]') ||
            target.closest('.MuiGrid2-root');

          if (isEmptySpace && !target.closest('[role="button"]') && !target.closest('[data-testid^="recipe-card-"]')) {
            handleGeneralContextMenu(e);
          }
        }}
      >
        {showCase?.length && !isSharedApps ? (
          <Box data-testid="showcase-recipes-container" sx={{ width: '100%' }}>
            <EducationGallery recipes={showCase} />
          </Box>
        ) : null}

        {isError ? (
          <FlexColCenHorVer sx={{ width: '100%', height: '100%' }}>
            <ErrorDisplay
              icon={<SmileyXEyes />}
              title={t(I18N_KEYS.MAIN_DASHBOARD.ERROR_LOADING_RECIPES.TITLE)}
              description={null}
              actions={[
                { label: t(I18N_KEYS.MAIN_DASHBOARD.ERROR_LOADING_RECIPES.ACTION), onClick: () => onReload?.() },
              ]}
            />
          </FlexColCenHorVer>
        ) : (
          <FlexCol
            sx={{ flexGrow: 1 }}
            onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
              // Handle right-click on empty space within the content area
              const target = e.target as HTMLElement;
              const isCard =
                target.closest('[data-testid^="recipe-card-"]') ||
                target.closest('[data-testid^="folder-card-"]') ||
                target.closest('tr[role="checkbox"]');

              if (!isCard) {
                e.stopPropagation();
                handleGeneralContextMenu(e);
              }
            }}
          >
            {renderSubheader()}
            {renderContent()}
          </FlexCol>
        )}
      </Box>
      <AppContextMenu
        key={`${contextMenu.recipeId}-${contextMenu.isFolder}`}
        mouseX={contextMenu.mouseX}
        mouseY={contextMenu.mouseY}
        open={(() => {
          if (!contextMenu.isOpen) return false;
          const items = getMenuItems(contextMenu);
          return items.length > 0;
        })()}
        onClose={handleContextMenuClose}
        items={useMemo(() => {
          if (!contextMenu.isOpen) return [];
          return getMenuItems(contextMenu);
        }, [contextMenu, getMenuItems])}
        width="200px"
      />
      <ConfirmationDialogV2
        open={deleteDialogState.kind !== 'closed'}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title={
          deleteDialogState.kind === 'folder'
            ? t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRMATION_DELETE_FOLDER)
            : !isSharedApps
              ? t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRMATION_DELETE_FILE)
              : t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRMATION_DELETE_APP)
        }
        message={
          deleteDialogState.kind === 'folder'
            ? t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.DELETE_FOLDER_MESSAGE)
            : undefined
        }
        confirmText={
          deleteDialogState.kind === 'folder'
            ? t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.DELETE_FOLDER_CONFIRM)
            : t(I18N_KEYS.MAIN_DASHBOARD.DELETE_DIALOG.CONFIRM)
        }
        withLoading
      />
      {changeOwnerDialogState.kind === 'open' && (
        <ChangeOwnerModal
          open
          onClose={() => setChangeOwnerDialogState({ kind: 'closed' })}
          recipeId={changeOwnerDialogState.id}
        />
      )}
      <CreateFolderModal
        open={isShowingCreateFolderDialog}
        onClose={() => setIsShowingCreateFolderDialog(false)}
        onConfirm={handleCreateFolder}
      />
      {moveDialogState.kind !== 'closed' && (
        <MoveModal
          open
          onClose={() => setMoveDialogState({ kind: 'closed' })}
          onConfirm={handleMove}
          itemName={moveDialogState.name}
          itemId={moveDialogState.id}
          isFolder={moveDialogState.kind === 'folder'}
          currentFolderId={currentFolderId}
          isWorkspaceScope={isWorkspaceFiles}
        />
      )}
    </>
  );
}
